import { t } from "@travily/i18n";
import { formatPriceWhole } from "@ui/lib/format-price";
import { WARM_THRESHOLD } from "@ui/utils/temperature-bands";
import type { WeatherIcon } from "./DestinationCard";

export type CostEvidenceGrade = "measured" | "modelled" | "inherited";

const APPROXIMATELY = "≈";

export interface DestinationTemperatureProps {
  temperature?: string;
  weatherIcon?: WeatherIcon;
}

/** Formats an average temperature in celsius into DestinationCard overlay props. */
export function resolveDestinationTemperature(avgTempCelsius: number | null | undefined): DestinationTemperatureProps {
  if (avgTempCelsius == null) return {};
  return {
    temperature: `${Math.round(avgTempCelsius)}°`,
    weatherIcon: avgTempCelsius >= WARM_THRESHOLD ? "sun" : "cloud",
  };
}

/** True for a figure nobody paid. A caller with no grade to pass says nothing
 *  either way, and its figure is left exactly as it was. */
export function isEstimatedCost(grade: CostEvidenceGrade | null | undefined): boolean {
  return grade != null && grade !== "measured";
}

/** How the figure was arrived at, in the words the price is labelled with. */
export function resolveDestinationPriceNote(grade: CostEvidenceGrade | null | undefined): string {
  return isEstimatedCost(grade)
    ? t("components.views.shared.destination_card.price_modelled")
    : t("components.views.shared.destination_card.price_measured");
}

/**
 * Formats the composed monthly cost to the source currency, whole units only
 * (no cents/pence). A measured figure shows exactly, because somebody paid it;
 * any other grade is approximated, because the exact figure would claim a
 * precision the evidence behind it does not have.
 */
export function resolveDestinationPrice(input: {
  cost_total_cents?: number | null;
  cost_currency?: string | null;
  cost_evidence_grade?: CostEvidenceGrade | null;
}): string | undefined {
  const cents = input.cost_total_cents;
  const currency = input.cost_currency;
  if (cents == null || !currency) return undefined;
  const prefix = isEstimatedCost(input.cost_evidence_grade) ? APPROXIMATELY : "";
  return `${prefix}${formatPriceWhole(cents, currency)}/mo`;
}
